import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import EditExpenseModal from "./EditExpenseModal";
import { adminApiRequest } from "../utils/adminApi";

const CATEGORY_OPTIONS = [
  { value: "purchase", label: "Purchase" },
  { value: "repair", label: "Repair" },
  { value: "maintenance", label: "Maintenance" },
  { value: "bills", label: "Bills" },
  { value: "wages", label: "Wages" },
  { value: "rent", label: "Rent" },
  { value: "other", label: "Other" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const iconForCategory = (cat) => {
  const c = (cat || "").toLowerCase();
  if (c.includes("wage")) return "fa-solid fa-user";
  if (c.includes("rent")) return "fa-solid fa-house";
  if (c.includes("bill")) return "fa-solid fa-file-invoice";
  if (c.includes("repair")) return "fa-solid fa-screwdriver-wrench";
  if (c.includes("maintenance")) return "fa-solid fa-toolbox";
  if (c.includes("purchase")) return "fa-solid fa-cart-shopping";
  return "fa-solid fa-receipt";
};

const normalizeExpense = (e) => ({
  id: e.id,
  expense_date: e.expense_date || e.date || e.created_at?.slice?.(0, 10) || "",
  category: e.category || "",
  amount: e.amount ?? e.expense_amount ?? 0,
  description: e.description || e.note || "",
});

export default function Expenses() {
  const [error, setError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);

  const [expenses, setExpenses] = useState([]);
  const [filterStart, setFilterStart] = useState(daysAgoISO(6));
  const [filterEnd, setFilterEnd] = useState(todayISO());

  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [formData, setFormData] = useState({
    expense_date: todayISO(),
    category: "",
    other_category: "",
    amount: "",
    description: "",
  });

  const finalCategory =
    formData.category === "other"
      ? (formData.other_category || "").trim()
      : formData.category;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      const category = String(exp.category || "").toLowerCase();
      const description = String(exp.description || "").toLowerCase();

      const matchesCategory = !categoryFilter
        ? true
        : category === categoryFilter.toLowerCase();

      const matchesSearch = !debouncedSearch
        ? true
        : category.includes(debouncedSearch) ||
          description.includes(debouncedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [expenses, categoryFilter, debouncedSearch]);

  const totalAmount = useMemo(
    () => filteredExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0),
    [filteredExpenses]
  );

  const setErrorWithTimeout = (msg) => {
    setError(msg);
    if (msg) setTimeout(() => setError(""), 3000);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const fetchExpenses = async (start = filterStart, end = filterEnd) => {
    setIsSubmitting(true);
    try {
      const data = await adminApiRequest({
        script: "fetch-expense",
        data: { start, end },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to fetch expenses");
      }

      const normalized = Array.isArray(data.data)
        ? data.data.map(normalizeExpense)
        : [];

      normalized.sort((a, b) =>
        (b.expense_date || "").localeCompare(a.expense_date || "")
      );

      setExpenses(normalized);
    } catch (e) {
      console.error("Error fetching expenses:", e?.message);
      setExpenses([]);
      setErrorWithTimeout(e?.message || "Error fetching expenses");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRecordModal = () => {
    setShowRecordModal(true);
    setError("");
  };

  const closeAllModals = () => {
    setShowRecordModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setExpenseToDelete(null);
    setExpenseToEdit(null);
    setError("");
  };

  const resetForm = () =>
    setFormData({
      expense_date: todayISO(),
      category: "",
      other_category: "",
      amount: "",
      description: "",
    });

  const openEditModal = (expense) => {
    setExpenseToEdit(expense);

    const catLower = (expense.category || "").trim().toLowerCase();
    const isKnown = CATEGORY_OPTIONS.some((c) => c.value === catLower);

    setFormData({
      expense_date: expense.expense_date || todayISO(),
      category: isKnown ? catLower : "other",
      other_category: isKnown ? "" : (expense.category || "").trim(),
      amount: String(expense.amount ?? ""),
      description: expense.description || "",
    });

    setShowEditModal(true);
    setError("");
  };

  const handleRecordExpense = async (e) => {
    e.preventDefault();

    if (!formData.expense_date) {
      return setErrorWithTimeout("Please select an expense date.");
    }
    if (!formData.category) {
      return setErrorWithTimeout("Please select a category.");
    }
    if (formData.category === "other" && !finalCategory) {
      return setErrorWithTimeout("Please enter the custom category name.");
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      return setErrorWithTimeout("Please enter a valid amount greater than 0.");
    }

    setIsSubmitting(true);

    try {
      const data = await adminApiRequest({
        script: "create-expense",
        data: {
          expense_date: formData.expense_date,
          category: finalCategory,
          amount: formData.amount,
          description: formData.description,
        },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to record expense");
      }

      showSuccess("Expense recorded successfully");
      resetForm();
      closeAllModals();
      fetchExpenses(filterStart, filterEnd);
    } catch (err) {
      console.error("Record expense error:", err?.message);
      setErrorWithTimeout(err?.message || "Error recording expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateExpense = async (e, finalCatFromModal) => {
    e.preventDefault();

    if (!expenseToEdit?.id) {
      return setErrorWithTimeout("Invalid expense selected.");
    }

    const finalCat = finalCatFromModal ?? finalCategory;

    if (!formData.expense_date) {
      return setErrorWithTimeout("Please select an expense date.");
    }
    if (!formData.category) {
      return setErrorWithTimeout("Please select a category.");
    }
    if (formData.category === "other" && !finalCat) {
      return setErrorWithTimeout("Please enter the custom category name.");
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      return setErrorWithTimeout("Please enter a valid amount greater than 0.");
    }

    setIsSubmitting(true);

    try {
      const data = await adminApiRequest({
        script: "update-expense",
        data: {
          id: expenseToEdit.id,
          expense_date: formData.expense_date,
          category: finalCat,
          amount: formData.amount,
          description: formData.description,
        },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to update expense");
      }

      showSuccess("Expense updated successfully");
      closeAllModals();
      resetForm();
      fetchExpenses(filterStart, filterEnd);
    } catch (err) {
      console.error("Update expense error:", err?.message);
      setErrorWithTimeout(err?.message || "Error updating expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteModal(true);
  };

  const confirmDeleteExpense = async (e) => {
    e.preventDefault();

    if (!expenseToDelete?.id) {
      setErrorWithTimeout("Invalid expense selected for deletion");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await adminApiRequest({
        script: "delete-expense",
        data: { id: expenseToDelete.id },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to delete expense");
      }

      showSuccess("Expense deleted successfully");
      closeAllModals();
      fetchExpenses(filterStart, filterEnd);
    } catch (err) {
      console.error("Delete expense error:", err?.message);
      setErrorWithTimeout(err?.message || "Error deleting expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyDateFilter = () => {
    if (filterStart && filterEnd && filterStart > filterEnd) {
      setErrorWithTimeout("Start date cannot be after end date.");
      return;
    }
    fetchExpenses(filterStart, filterEnd);
  };

  const resetDateFilter = () => {
    const start = daysAgoISO(6);
    const end = todayISO();
    setFilterStart(start);
    setFilterEnd(end);
    fetchExpenses(start, end);
  };

  const resetSearchAndCategory = () => {
    setCategoryFilter("");
    setSearch("");
    setDebouncedSearch("");
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-2">
        {isSubmitting && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
          </div>
        )}

        {showSuccessMessage && (
          <div className="fixed right-6 top-6 z-50 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg">
            {successMessage}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Expenses
          </h1>

          <button
            onClick={openRecordModal}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            + Add Expense
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              From
            </label>
            <input
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              className="block rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              To
            </label>
            <input
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              className="block rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={applyDateFilter}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Apply
            </button>

            <button
              onClick={resetDateFilter}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Reset
            </button>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category or description..."
              className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={resetSearchAndCategory}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Clear Search
          </button>

          <div className="ml-auto text-lg font-semibold text-black">
            Total: £{formatMoney(totalAmount)} ({filteredExpenses.length} records)
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filteredExpenses.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              No expenses found
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredExpenses.map((exp) => (
                <div
                  key={exp.id}
                  className="flex flex-col gap-4 p-4 transition hover:bg-slate-50 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                      <i className={iconForCategory(exp.category)}></i>
                    </div>

                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold capitalize text-slate-900">
                          {exp.category}
                        </span>

                        <span className="text-xs text-slate-500">
                          {exp.expense_date}
                        </span>
                      </div>

                      <p className="text-sm text-slate-500">
                        {exp.description || "No description"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-lg font-bold text-slate-900">
                      £{formatMoney(exp.amount)}
                    </div>

                    <button
                      onClick={() => openEditModal(exp)}
                      className="rounded-lg bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700"
                      title="Edit"
                    >
                      <i className="fas fa-pen"></i>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(exp)}
                      className="rounded-lg bg-red-600 px-3 py-1 text-white hover:bg-red-700"
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showRecordModal && (
          <Modal
            title="Add Expense"
            onClose={() => {
              closeAllModals();
              resetForm();
            }}
            onSubmit={handleRecordExpense}
            submitButtonText={isSubmitting ? "Saving..." : "Save Expense"}
            showCancel={true}
            showSubmitButton={true}
            className="max-w-2xl"
            submitButtonClass="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, expense_date: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Amount (£) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, amount: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((p) => ({
                        ...p,
                        category: v,
                        other_category: v === "other" ? p.other_category : "",
                      }));
                    }}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                  >
                    <option value="">Select Category</option>
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.category === "other" ? (
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Other Category *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Generator Fuel"
                      value={formData.other_category}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          other_category: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                    />
                  </div>
                ) : (
                  <div />
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Description / Note
                </label>
                <textarea
                  placeholder="Write a short note about this expense..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                />
              </div>
            </div>
          </Modal>
        )}

        <EditExpenseModal
          isOpen={showEditModal}
          isSubmitting={isSubmitting}
          formData={formData}
          setFormData={setFormData}
          onClose={() => {
            closeAllModals();
            resetForm();
          }}
          onSubmit={handleUpdateExpense}
        />

        {showDeleteModal && (
          <Modal
            title="Delete Expense"
            onClose={closeAllModals}
            onSubmit={confirmDeleteExpense}
            submitButtonText="Delete Expense"
            showCancel={true}
            showSubmitButton={true}
            className="max-w-md"
            submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          >
            <div className="space-y-3 text-sm text-slate-600">
              <p>Are you sure you want to permanently delete this expense?</p>

              {expenseToDelete && (
                <p className="rounded-xl bg-slate-50 p-3">
                  <strong>{expenseToDelete.category}</strong> — £
                  {formatMoney(expenseToDelete.amount)} on{" "}
                  {expenseToDelete.expense_date}
                </p>
              )}
            </div>
          </Modal>
        )}

        {error && (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-red-600 px-4 py-3 text-white shadow">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}